import React, { useCallback, useRef, useState, useEffect } from "react";
import { ChevronRight, X, Zap } from "lucide-react";
import { useFileStore } from "../stores/fileStore";
import {
  loadPickAndPlaceSceneDemo,
  loadPandaPickAndPlaceSceneDemo,
  loadSceneRecipeById,
} from "../utils/sceneRecipes";
import { useFocusTrap } from "../hooks/useFocusTrap";
import {
  readReducedMotionPreference,
  subscribeReducedMotionPreference,
} from "../utils/reducedMotionPreference";
import { LoadingProgressBar } from "./draggable-panels/object-library/MobileLibraryOverlays";
import Tooltip from "./ui/Tooltip";
import { resolveCritterAssetsUrl } from "../utils/assetPaths";

function RedCubeIcon({ size = 16 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      {/* front face */}
      <polygon points="1,7 9,7 9,15 1,15" fill="#ef4444" />
      {/* top face */}
      <polygon points="1,7 5,3 13,3 9,7" fill="#f87171" />
      {/* right face */}
      <polygon points="9,7 13,3 13,11 9,15" fill="#b91c1c" />
    </svg>
  );
}

const robots = [
  {
    id: "panda",
    companyLine: "Franka Emika",
    modelLine: "Panda",
    thumbnail: resolveCritterAssetsUrl("/robots/franka_emika_panda/thumbnail-pick-and-place.png"),
    load: loadPandaPickAndPlaceSceneDemo,
  },
  {
    id: "ur5e",
    companyLine: "Universal Robotics",
    modelLine: "UR5e",
    thumbnail: resolveCritterAssetsUrl("/robots/ur5e/thumbnail-pick-and-place.png"),
    load: loadPickAndPlaceSceneDemo,
  },
  {
    id: "spot_arm",
    companyLine: "Boston Dynamics",
    modelLine: "Spot",
    thumbnail: resolveCritterAssetsUrl("/robots/boston_dynamics_spot/thumbnail-pick-and-place.png"),
    load: (options) => loadSceneRecipeById("pick_and_place_spot_arm_no_tray", options),
  },
];

function shouldShowOnboarding() {
  const searchParams = new URLSearchParams(window.location.search);
  const forceOn = searchParams.get("onboarding") === "1";
  const flagOn = import.meta.env.VITE_ENABLE_ONBOARDING === "true";
  return Boolean(flagOn || forceOn);
}

function OnboardingCollapsedTab({ onOpen }) {
  return (
    <span
      className="fixed inline-flex"
      style={{
        left: 0,
        bottom: 16,
        zIndex: 200,
      }}
    >
      <Tooltip content="Open onboarding menu" side="right">
        <button
          type="button"
          onClick={onOpen}
          className="flex flex-row items-center justify-center gap-0.5 rounded-r-md border border-l-0 border-gray-700 text-gray-300 transition-colors cursor-pointer hover:text-gray-100"
          style={{
            width: "3rem",
            height: "2.5rem",
            backgroundColor: "var(--panel-surface)",
          }}
          aria-label="Open onboarding menu"
        >
          <span className="flex items-center justify-center text-gray-200">
            <RedCubeIcon size={16} />
          </span>
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
        </button>
      </Tooltip>
    </span>
  );
}

export default function OnboardingOverlay() {
  const [enabled] = useState(() => shouldShowOnboarding());
  const [visible, setVisible] = useState(enabled);
  const [loading, setLoading] = useState(null);
  const [loadingProgressPct, setLoadingProgressPct] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(readReducedMotionPreference);
  const dialogRef = useRef(null);
  const xmlFile = useFileStore((s) => s.xmlFile);

  const dismiss = useCallback(() => {
    setVisible(false);
  }, []);

  const open = useCallback(() => {
    setVisible(true);
  }, []);

  // Auto-dismiss if a scene is loaded externally (drag-and-drop, direct file open, etc.)
  useEffect(() => {
    if (visible && xmlFile) {
      dismiss();
    }
  }, [dismiss, xmlFile, visible]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        dismiss();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dismiss, visible]);

  useEffect(() => subscribeReducedMotionPreference(setReducedMotion), []);

  useFocusTrap(dialogRef, visible);

  if (!enabled) return null;
  if (!visible) return <OnboardingCollapsedTab onOpen={open} />;

  async function handleRobotClick(robot) {
    if (loading) return;
    setLoading(robot.id);
    setLoadingProgressPct(0);
    try {
      await robot.load({
        onProgress: (progress) =>
          setLoadingProgressPct(Math.round(100 * Math.min(1, Math.max(0, progress)))),
      });
    } finally {
      setLoading(null);
      setLoadingProgressPct(0);
      dismiss();
    }
  }

  return (
    // Backdrop — click outside the card to permanently dismiss
    <div
      onClick={dismiss}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.35)",
      }}
    >
      {/* Card — stop clicks from reaching the backdrop */}
      <div
        ref={dialogRef}
        className="menu-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        tabIndex={-1}
        style={{
          position: "relative",
          padding: "32px 36px 36px",
          maxWidth: 640,
          width: "calc(100vw - 48px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 24,
        }}
      >
        {/* Top-right: dismiss */}
        <button
          type="button"
          className="absolute right-3 top-3 rounded p-1 text-gray-400 transition-colors hover:text-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
          onClick={dismiss}
          aria-label="Dismiss onboarding"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>

        {/* Heading */}
        <div style={{ textAlign: "center" }}>
          <h2
            id="onboarding-title"
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 600,
              color: "var(--panel-text-primary)",
              letterSpacing: "-0.01em",
            }}
          >
            Pick your Robot
          </h2>
          <p
            className="menu-text-muted"
            style={{
              margin: "8px 0 0",
              fontSize: 14,
              lineHeight: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              flexWrap: "wrap",
            }}
          >
            Can you use the robot to pick up the red cube?
            <RedCubeIcon size={13} />
          </p>
        </div>

        {/* Robot cards */}
        <div
          style={{
            display: "flex",
            gap: 16,
            width: "100%",
            justifyContent: "center",
          }}
        >
          {robots.map((robot) => (
            <button
              key={robot.id}
              onClick={() => handleRobotClick(robot)}
              disabled={loading !== null}
              style={{
                flex: "1 1 0",
                minWidth: 0,
                background: "transparent",
                border: "1px solid var(--panel-border)",
                borderRadius: 8,
                padding: "12px 12px 14px",
                cursor: loading ? "wait" : "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                transition: reducedMotion ? "none" : "background 0.15s, border-color 0.15s",
                opacity: loading && loading !== robot.id ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = "var(--panel-surface-hover)";
                  e.currentTarget.style.borderColor = "rgba(120, 120, 120, 1)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.borderColor = "var(--panel-border)";
              }}
            >
              <img
                src={robot.thumbnail}
                alt={`${robot.modelLine} · ${robot.companyLine}`}
                style={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  objectFit: "cover",
                  borderRadius: 6,
                  display: "block",
                }}
              />
              {loading === robot.id ? (
                <span className="flex w-full flex-col items-stretch gap-2">
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "rgb(156, 163, 175)",
                    }}
                  >
                    Loading...
                  </span>
                  <span className="flex w-full items-center gap-2">
                    <LoadingProgressBar
                      progressPct={loadingProgressPct}
                      ariaLabel={`${robot.modelLine} loading progress`}
                      className="h-1.5 flex-1"
                    />
                    <span className="min-w-8 text-right text-[10px] font-medium tabular-nums text-gray-400">
                      {Math.round(Math.min(100, Math.max(0, loadingProgressPct)))}%
                    </span>
                  </span>
                </span>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    textAlign: "center",
                    lineHeight: 1.25,
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--panel-text-primary)",
                    }}
                  >
                    {robot.modelLine}
                  </span>
                  <span
                    className="menu-text-muted"
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {robot.companyLine}
                  </span>
                </div>
              )}
            </button>
          ))}
        </div>

        <details
          style={{
            width: "100%",
            marginTop: -8,
          }}
        >
          <summary
            className="menu-text-muted"
            style={{
              padding: "8px 10px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.4,
              userSelect: "none",
            }}
          >
            Hint
          </summary>
          <div
            className="menu-text-muted"
            style={{
              margin: 0,
              padding: "0 10px 10px",
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            <div>1. Start physics (space bar)</div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span>2. Search the menus for the actuator sliders</span>
              <Zap className="w-3 h-3 shrink-0 text-amber-500" aria-hidden />
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}
